import React from 'react';
import PropTypes from 'prop-types';
import { getBaseButtonClasses, getFocusRingClasses, wrapWithLoadingSpinner, getIconSpacingClasses } from './buttonUtils';

/**
 * SuccessButton Component
 * 
 * A success/positive action button with green styling.
 * Used for create actions, confirmations, and positive outcomes.
 * 
 * @example
 * // Basic success button
 * <SuccessButton onClick={handleCreate}>
 *   Create Website
 * </SuccessButton>
 * 
 * @example
 * // With icon and loading
 * <SuccessButton
 *   leftIcon={<PlusIcon />}
 *   loading={isCreating}
 *   size="lg"
 * >
 *   Create New
 * </SuccessButton>
 * 
 * @example
 * // Outline variant
 * <SuccessButton variant="outline" onClick={handleConfirm}>
 *   Confirm
 * </SuccessButton>
 */
const SuccessButton = ({
  children,
  onClick,
  type = 'button',
  variant = 'solid',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  leftIcon = null,
  rightIcon = null,
  className = '',
  ...rest
}) => {
  const baseClasses = getBaseButtonClasses({ size, disabled, fullWidth, loading });
  const focusRingClasses = getFocusRingClasses('success');

  // Variant-specific classes
  const variantClasses = {
    solid: [
      'bg-green-400',
      'hover:bg-green-500',
      'text-white',
      'shadow-sm',
      'hover:shadow-md',
    ],
    outline: [
      'bg-white',
      'hover:bg-green-50',
      'text-green-600',
      'border',
      'border-green-300',
      'hover:border-green-400',
    ],
  };

  const classes = [
    baseClasses,
    ...(variantClasses[variant] || variantClasses.solid),
    focusRingClasses,
    className,
  ].join(' ');

  const buttonContent = (
    <>
      {!loading && leftIcon && (
        <span className={getIconSpacingClasses('left', !!children)}>
          {leftIcon}
        </span>
      )}
      {wrapWithLoadingSpinner(loading, children, size)}
      {!loading && rightIcon && (
        <span className={getIconSpacingClasses('right', !!children)}>
          {rightIcon}
        </span>
      )}
    </>
  );

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={classes}
      aria-busy={loading}
      {...rest}
    >
      {buttonContent}
    </button>
  );
};

SuccessButton.propTypes = {
  /** Button content */
  children: PropTypes.node,
  /** Click handler */
  onClick: PropTypes.func,
  /** Button type */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  /** Visual variant */
  variant: PropTypes.oneOf(['solid', 'outline']),
  /** Button size */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Loading state */
  loading: PropTypes.bool,
  /** Full width button */
  fullWidth: PropTypes.bool,
  /** Icon to display on the left */
  leftIcon: PropTypes.node,
  /** Icon to display on the right */
  rightIcon: PropTypes.node,
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default SuccessButton;
